/**
 * DepartmentScheduleDialog — AI-generated 7-day shift schedule per department.
 *
 * Inputs that drive the schedule:
 *   • Team roster — saved per-department (name + optional position). Editors
 *     can add/remove rows and Save the roster; on next open the saved roster
 *     hydrates automatically. The roster IS the headcount.
 *   • Daily occupancy (Mon-Sun) — 0-100% per day.
 *   • Daily restaurant bookings (Mon-Sun) — manual count per day; will be
 *     wired to the restaurant booking system later.
 *   • Positions list (comma-separated) — the roles that need coverage.
 *   • GM notes — freeform context.
 *
 * The backend (`POST /api/admin/departments/{deptId}/ai-schedule`) calls
 * GPT-5.2 with these factors and returns a structured `shifts` array.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { CalendarDays, Sparkles, Loader2, Printer, Save, Users, Plus, Trash2, Download, FileSpreadsheet, FileText as FileWord, FileText as FilePdf } from 'lucide-react';
import { printHtml } from '../lib/print';
import {
  buildScheduleMatrix,
  buildScheduleMatrixHtml,
  exportScheduleExcel,
  exportSchedulePdf,
  exportScheduleWord,
} from '../lib/scheduleExport';
import { buildSnapshotBranding } from '../lib/snapshotBranding';
import DocumentHeader from './DocumentHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function todayMonday() {
  const d = new Date();
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - (day - 1));
  return d.toISOString().slice(0, 10);
}

export default function DepartmentScheduleDialog({ open, onClose, deptId, deptLabel }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  // F&B-level depts get an extra "Restaurant" selector so the schedule can
  // be tagged to a specific concept (the team roster is per-restaurant when
  // a restaurant is picked, otherwise it falls back to the dept-wide roster).
  const isFbScope = (deptId || '').startsWith('fb-workspace');
  // When opened from a RestaurantDetailDialog, deptId is `restaurant.{uuid}` —
  // we fetch that specific outlet's logo + accent so the header + print
  // are properly branded.
  const isRestaurantScope = (deptId || '').startsWith('restaurant.');
  const restaurantIdFromScope = isRestaurantScope ? deptId.replace(/^restaurant\./, '') : '';
  const [restaurants, setRestaurants] = useState([]); // [{id, name}]
  const [restaurantId, setRestaurantId] = useState(restaurantIdFromScope || '');
  const [scopedOutlet, setScopedOutlet] = useState(null); // full outlet doc when restaurant-scoped
  const [positions, setPositions] = useState('');
  const [weekStart, setWeekStart] = useState(todayMonday());
  const [dailyOcc, setDailyOcc] = useState([60, 60, 60, 65, 80, 90, 75]);
  const [dailyBook, setDailyBook] = useState([0, 0, 0, 0, 0, 0, 0]);
  const [notes, setNotes] = useState('');
  const [weeklyBudget, setWeeklyBudget] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [team, setTeam] = useState([]); // [{name, position, pay_rate, pay_type, contracted_hours}]
  const [savedTeamHash, setSavedTeamHash] = useState('');
  const [busy, setBusy] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  // Custom document name — pre-filled with an auto-generated default and
  // persisted via DocumentHeader. Flows into print/export filenames and
  // the snapshot title default.
  const [docName, setDocName] = useState('');

  const teamDirty = JSON.stringify(team) !== savedTeamHash;
  // The effective scope key — when a restaurant is picked under F&B, team
  // and schedule are scoped to that restaurant. Otherwise to the parent
  // department (back-compat for non-F&B depts).
  const scopeId = isFbScope && restaurantId ? `${deptId}__${restaurantId}` : deptId;
  const restaurantName = scopedOutlet?.name || restaurants.find((r) => r.id === restaurantId)?.name || '';

  // Fetch restaurant concepts when needed (F&B picker mode OR fetch the
  // single outlet when we're already scoped to one).
  useEffect(() => {
    if (!open) return;
    if (!isFbScope && !isRestaurantScope) return;
    axios.get(`${API}/property`).then(({ data }) => {
      const list = (data?.fnb || []);
      setRestaurants(list.map((o) => ({ id: o.id, name: o.name, logo_url: o.logo_url, accent_color: o.accent_color })));
      if (isRestaurantScope) {
        const match = list.find((o) => o.id === restaurantIdFromScope);
        setScopedOutlet(match || null);
      }
    }).catch(() => setRestaurants([]));
  }, [open, isFbScope, isRestaurantScope, restaurantIdFromScope]);

  // Load saved team roster on open (re-loads when restaurant changes for F&B).
  useEffect(() => {
    if (!open || !adminToken) return;
    axios.get(`${API}/departments/${encodeURIComponent(scopeId)}/team`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(({ data }) => {
        const members = data.members || [];
        setTeam(members);
        setSavedTeamHash(JSON.stringify(members));
      })
      .catch(() => { setTeam([]); setSavedTeamHash('[]'); });
  }, [open, adminToken, scopeId]);

  const addMember = () => setTeam((t) => [...t, { name: '', position: '', pay_rate: '', pay_type: 'hourly' }]);
  const removeMember = (idx) => setTeam((t) => t.filter((_, i) => i !== idx));
  const updateMember = (idx, patch) => setTeam((t) => t.map((m, i) => (i === idx ? { ...m, ...patch } : m)));

  // Close the export menu on outside click (matches InventoryTable pattern).
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onDocClick = () => setExportOpen(false);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [exportOpen]);

  const saveTeam = async () => {
    setTeamSaving(true);
    try {
      const cleaned = team.filter((m) => (m.name || '').trim());
      const { data } = await axios.put(
        `${API}/admin/departments/${encodeURIComponent(scopeId)}/team`,
        { members: cleaned },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      const members = data.members || [];
      setTeam(members);
      setSavedTeamHash(JSON.stringify(members));
      toast.success(`Team roster saved (${members.length} member${members.length === 1 ? '' : 's'})${restaurantName ? ` for ${restaurantName}` : ''}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save team');
    } finally {
      setTeamSaving(false);
    }
  };

  const generate = async () => {
    if (!adminToken) { toast.error('Sign in to generate a schedule.'); return; }
    if (isFbScope && !restaurantId) { toast.error('Pick a restaurant first.'); return; }
    if (!positions.trim()) { toast.error('List at least one position.'); return; }
    const namedTeam = team.filter((m) => (m.name || '').trim());
    if (namedTeam.length === 0) { toast.error('Add and save at least one team member first.'); return; }
    if (teamDirty) { toast.error('Save the team roster before generating.'); return; }
    setBusy(true);
    try {
      const team_members = namedTeam.map((m) => m.position ? `${m.name} · ${m.position}` : m.name);
      const { data } = await axios.post(
        `${API}/admin/departments/${encodeURIComponent(scopeId)}/ai-schedule`,
        {
          positions,
          week_start: weekStart,
          daily_occupancy: dailyOcc.map((v) => Number(v) || 0),
          daily_restaurant_bookings: dailyBook.map((v) => Number(v) || 0),
          team_members,
          notes: restaurantName ? `Restaurant: ${restaurantName}.\n${notes || ''}` : notes,
          weekly_budget: weeklyBudget !== '' ? Number(weeklyBudget) : null,
          currency,
        },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      setSchedule({ ...data, restaurant_name: restaurantName, restaurant_id: restaurantId });
      toast.success(`Schedule generated${restaurantName ? ` for ${restaurantName}` : ''}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI generation failed');
    } finally {
      setBusy(false);
    }
  };

  const printSchedule = () => {
    if (!schedule) return;
    const restaurant = scopedOutlet || restaurants.find((r) => r.id === restaurantId);
    const logoUrl = restaurant?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${restaurant.logo_url}` : '';
    const accent = restaurant?.accent_color || '#003DA5';
    const body = buildScheduleMatrixHtml({
      schedule, team, weekStart,
      deptLabel: deptLabel || deptId,
      restaurant: restaurant || null,
      logoUrl,
      accent,
      docName,
    });
    printHtml(body, docName || `${schedule.restaurant_name || deptLabel || deptId} schedule`);
  };

  // Export the matrix to PDF / Excel / Word. We always reuse the saved team
  // roster as the row order so an empty schedule still produces a valid sheet
  // (every staff member visible, all 7 day cells showing "OFF").
  const restaurantForExport = scopedOutlet || restaurants.find((r) => r.id === restaurantId) || null;
  const exportArgs = () => ({
    schedule,
    team,
    weekStart,
    deptLabel: deptLabel || deptId,
    restaurant: restaurantForExport,
    docName,
  });
  const handleExport = async (format) => {
    if (!schedule) return;
    try {
      if (format === 'excel') await exportScheduleExcel(exportArgs());
      else if (format === 'pdf') await exportSchedulePdf(exportArgs());
      else if (format === 'word') exportScheduleWord(exportArgs());
      toast.success(`Schedule exported as ${format.toUpperCase()}.`);
    } catch (e) {
      toast.error(`Export failed: ${e?.message || 'unknown error'}`);
    }
  };

  const saveSnapshot = async () => {
    if (!schedule) return;
    const defaultTitle = docName || `${deptLabel || deptId} schedule · ${weekStart}`;
    const title = window.prompt('Name this saved schedule:', defaultTitle);
    if (!title) return;
    try {
      // Branding (hotel + restaurant when scope is restaurant.{id}) so the
      // viewer + print path can render a branded header from the stored
      // snapshot — without re-fetching the property.
      const outletForBrand = scopedOutlet || restaurants.find((r) => r.id === restaurantId) || null;
      const branding = await buildSnapshotBranding(outletForBrand);
      await axios.post(
        `${API}/admin/snapshots`,
        {
          // Schedules are now a first-class snapshot kind so the viewer
          // can render the original matrix table (was 'inventory' before,
          // which forced a generic key/value table render).
          kind: 'schedule',
          title,
          owner_node_id: deptId,
          payload: {
            // Persist the full schedule object so the viewer can rebuild
            // the staff×day matrix exactly as it appeared on screen.
            schedule,
            team,
            week_start: weekStart,
            dept_label: deptLabel || deptId,
            branding,
          },
          shared: false,
        },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      // Notify any open scoped saved-files list to refresh.
      window.dispatchEvent(new CustomEvent('snapshot-saved'));
      toast.success('Schedule saved to this department\'s Saved Files.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[94vh] w-[95vw] sm:w-auto overflow-hidden flex flex-col p-3 sm:p-6" data-testid="dept-schedule-dialog" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Manrope' }}>
            {((isFbScope && restaurantId) || isRestaurantScope) && (() => {
              const r = scopedOutlet || restaurants.find((x) => x.id === restaurantId);
              return r?.logo_url ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${r.logo_url}`}
                  alt={`${r.name} logo`}
                  className="w-10 h-10 rounded-lg object-contain"
                  style={{ background: 'white', border: `1px solid ${r.accent_color || '#003DA5'}30`, padding: 2 }}
                  data-testid="schedule-dialog-logo"
                />
              ) : null;
            })()}
            <CalendarDays className="w-5 h-5 text-blue-600" />
            AI Schedule · {restaurantName || deptLabel || deptId}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500" style={{ fontFamily: 'Manrope' }}>
            Generate a 7-day shift schedule from your saved team, daily occupancy, and restaurant bookings. Powered by GPT-5.2 — uses your AI Credentials when set.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* Branded centered banner + editable Document name. The name
              flows into prints (page title), exports (filename), and
              snapshot defaults. Scope key is per-dept (+ per-restaurant
              when F&B). */}
          <DocumentHeader
            scopeId={`schedule.${scopeId}.${weekStart}`}
            autoName={`${restaurantName || deptLabel || deptId} schedule · ${weekStart}`}
            outlet={scopedOutlet || restaurants.find((r) => r.id === restaurantId) || null}
            deptLabel={deptLabel || deptId}
            onChange={setDocName}
            readOnly={!adminToken}
          />

          {/* F&B restaurant selector — only shown when this schedule is being
              built from the F&B workspace (Culinary or FOH). Each restaurant
              gets its own team roster and its own schedule history. */}
          {isFbScope && (
            <section data-testid="schedule-restaurant-section" className="rounded-xl p-3" style={{ backgroundColor: '#FAF5E6', border: '1px solid #E2C97A' }}>
              <label className="block">
                <span className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#B45309', fontFamily: 'Manrope' }}>
                  Restaurant concept (required)
                </span>
                <select
                  data-testid="schedule-restaurant-select"
                  value={restaurantId}
                  onChange={(e) => setRestaurantId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm border outline-none font-bold"
                  style={{ borderColor: '#D4B86A', backgroundColor: 'white', color: '#1A2E2E', fontFamily: 'Manrope' }}
                >
                  <option value="">— Select restaurant —</option>
                  {restaurants.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </label>
              {!restaurantId && <p className="text-[10px] mt-1.5" style={{ color: '#92400E' }}>The team roster and schedule below will be tied to the restaurant you pick.</p>}
              {restaurantId && <p className="text-[10px] mt-1.5" style={{ color: '#15803D' }}>✓ Scoped to {restaurantName}. Schedules and rosters are unique per restaurant.</p>}
            </section>
          )}

          {/* TEAM ROSTER */}
          <section data-testid="schedule-team-section" className="rounded-xl p-3" style={{ backgroundColor: '#FAF8F2', border: '1px solid #E2DBC8' }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-700" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-blue-700" style={{ fontFamily: 'Manrope' }}>Team roster · {team.filter((m) => m.name?.trim()).length} member{team.filter((m) => m.name?.trim()).length === 1 ? '' : 's'}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" data-testid="schedule-team-add" onClick={addMember} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold" style={{ backgroundColor: '#A3D4D420', color: '#003DA5' }}>
                  <Plus className="w-3 h-3" /> Add
                </button>
                <button type="button" data-testid="schedule-team-save" onClick={saveTeam} disabled={teamSaving || !teamDirty}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold text-white disabled:opacity-40"
                  style={{ backgroundColor: '#15803D', fontFamily: 'Manrope' }}>
                  {teamSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} {teamDirty ? 'Save team' : 'Saved'}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mb-2">Save the team once — names will pre-fill the next time you open the schedule.</p>
            {team.length === 0 && <p className="text-[11px] text-gray-500 py-2">No team members yet. Click + Add to start.</p>}
            <div className="space-y-1.5">
              {team.map((m, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-1.5 items-center">
                  <input
                    data-testid={`schedule-team-name-${idx}`}
                    value={m.name}
                    onChange={(e) => updateMember(idx, { name: e.target.value })}
                    placeholder="Full name"
                    className="col-span-4 px-2 py-1 rounded text-xs border outline-none"
                    style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
                  />
                  <input
                    data-testid={`schedule-team-position-${idx}`}
                    value={m.position || ''}
                    onChange={(e) => updateMember(idx, { position: e.target.value })}
                    placeholder="Position"
                    className="col-span-3 px-2 py-1 rounded text-xs border outline-none"
                    style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
                  />
                  <input
                    data-testid={`schedule-team-rate-${idx}`}
                    type="number" min={0} step="0.01"
                    value={m.pay_rate ?? ''}
                    onChange={(e) => updateMember(idx, { pay_rate: e.target.value })}
                    placeholder="Rate/hr"
                    title="Hourly pay rate (salaried staff: pro-rata per hour)"
                    className="col-span-2 px-2 py-1 rounded text-xs border outline-none text-right"
                    style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
                  />
                  <select
                    data-testid={`schedule-team-paytype-${idx}`}
                    value={m.pay_type || 'hourly'}
                    onChange={(e) => updateMember(idx, { pay_type: e.target.value })}
                    className="col-span-2 px-1 py-1 rounded text-xs border outline-none"
                    style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
                  >
                    <option value="hourly">Hourly</option>
                    <option value="salary">Salary</option>
                  </select>
                  <button type="button" data-testid={`schedule-team-remove-${idx}`} onClick={() => removeMember(idx)} className="col-span-1 p-1 rounded hover:bg-red-50 justify-self-center" title="Remove">
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* DAILY FORECAST */}
          <section data-testid="schedule-forecast-section" className="rounded-xl p-3" style={{ backgroundColor: '#EAF2FF', border: '1px solid #C9D6E6' }}>
            <h3 className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2" style={{ fontFamily: 'Manrope' }}>Daily forecast (Mon → Sun)</h3>
            <div className="grid grid-cols-7 gap-1.5">
              {DAYS.map((d, i) => (
                <div key={d} className="space-y-1">
                  <p className="text-[10px] font-bold text-center text-blue-700">{d}</p>
                  <label className="block">
                    <span className="block text-[9px] text-gray-600 mb-0.5">Occ %</span>
                    <input
                      data-testid={`schedule-occ-${i}`}
                      type="number" min={0} max={100}
                      value={dailyOcc[i]}
                      onChange={(e) => setDailyOcc((arr) => arr.map((v, j) => (j === i ? e.target.value : v)))}
                      className="w-full px-1 py-1 rounded text-xs border outline-none text-center"
                      style={{ borderColor: '#C9D6E6', fontFamily: 'Manrope' }}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-[9px] text-gray-600 mb-0.5">Bookings</span>
                    <input
                      data-testid={`schedule-book-${i}`}
                      type="number" min={0}
                      value={dailyBook[i]}
                      onChange={(e) => setDailyBook((arr) => arr.map((v, j) => (j === i ? e.target.value : v)))}
                      className="w-full px-1 py-1 rounded text-xs border outline-none text-center"
                      style={{ borderColor: '#C9D6E6', fontFamily: 'Manrope' }}
                    />
                  </label>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-gray-500 mt-2">
              <strong>Bookings</strong> = number of guests booked in your restaurant(s) for that day. Manual entry for now — will be auto-fed from the restaurant booking system later.
            </p>
          </section>

          {/* POSITIONS + WEEK + NOTES */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Week start (Monday)">
              <input
                data-testid="schedule-week" type="date" value={weekStart}
                onChange={(e) => setWeekStart(e.target.value)}
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
                style={{ borderColor: '#D6D0C0' }}
              />
            </Field>
            <Field label="Positions (comma-separated)">
              <input
                data-testid="schedule-positions" type="text" value={positions}
                onChange={(e) => setPositions(e.target.value)}
                placeholder="Server, Bartender, Host"
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none"
                style={{ borderColor: '#D6D0C0' }}
              />
            </Field>
            <Field label="Weekly labor budget (optional)">
              <div className="flex gap-1.5">
                <select
                  data-testid="schedule-currency" value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="px-2 py-2 rounded-lg text-sm border outline-none font-bold"
                  style={{ borderColor: '#D6D0C0' }}
                >
                  <option value="USD">USD</option>
                  <option value="JMD">JMD</option>
                </select>
                <input
                  data-testid="schedule-budget" type="number" min={0} step="0.01" value={weeklyBudget}
                  onChange={(e) => setWeeklyBudget(e.target.value)}
                  placeholder="e.g. 5000"
                  className="flex-1 px-3 py-2 rounded-lg text-sm border outline-none"
                  style={{ borderColor: '#D6D0C0' }}
                />
              </div>
            </Field>
            <Field label="GM notes (optional)" full>
              <textarea
                data-testid="schedule-notes" rows={2} value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="VIP arrival Thu evening · Sat brunch peak · Sunday brunch reservation 60 covers"
                className="w-full px-3 py-2 rounded-lg text-sm border outline-none resize-none"
                style={{ borderColor: '#D6D0C0' }}
              />
            </Field>
          </section>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-gray-200">
          <button
            type="button" onClick={generate} disabled={busy}
            data-testid="schedule-generate"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white disabled:opacity-50 transition hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(99,102,241,0.35)' }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {busy ? 'Generating (~10-20s)…' : 'Generate schedule'}
          </button>
          {schedule && (
            <>
              <button type="button" onClick={printSchedule} data-testid="schedule-print" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}>
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  data-testid="schedule-export-btn"
                  onClick={() => setExportOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: 'white', color: '#003DA5', border: '1px solid #A3C4EE', fontFamily: 'Manrope' }}
                >
                  <Download className="w-3.5 h-3.5" /> Export
                  <span className="text-[8px] opacity-70">▾</span>
                </button>
                {exportOpen && (
                  <div
                    className="absolute right-0 bottom-full mb-1 z-50 rounded-lg overflow-hidden"
                    style={{ backgroundColor: 'white', border: '1px solid #D9E3F1', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', minWidth: 160 }}
                    data-testid="schedule-export-menu"
                  >
                    <ExportItem
                      label="PDF (.pdf)"
                      icon={<FilePdf className="w-3.5 h-3.5" />}
                      color="#DC2626"
                      testid="schedule-export-pdf"
                      onClick={() => { handleExport('pdf'); setExportOpen(false); }}
                    />
                    <ExportItem
                      label="Excel (.xlsx)"
                      icon={<FileSpreadsheet className="w-3.5 h-3.5" />}
                      color="#16A34A"
                      testid="schedule-export-excel"
                      onClick={() => { handleExport('excel'); setExportOpen(false); }}
                    />
                    <ExportItem
                      label="Word (.doc)"
                      icon={<FileWord className="w-3.5 h-3.5" />}
                      color="#1F4FBE"
                      testid="schedule-export-word"
                      onClick={() => { handleExport('word'); setExportOpen(false); }}
                    />
                  </div>
                )}
              </div>
              <button type="button" onClick={saveSnapshot} data-testid="schedule-save-snapshot" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold" style={{ backgroundColor: '#22C55E20', color: '#15803D', border: '1px solid #BBF7D0', fontFamily: 'Manrope' }}>
                <Save className="w-3.5 h-3.5" /> Save snapshot
              </button>
            </>
          )}
        </div>

        {schedule && (() => {
          const restaurantForMatrix = scopedOutlet || restaurants.find((r) => r.id === restaurantId) || null;
          const accent = restaurantForMatrix?.accent_color || '#003DA5';
          const { rows: matrixRows, dayHeaders, matrix, totals } = buildScheduleMatrix({ schedule, team, weekStart });
          const fmtCell = (list) => (list || []).map((s) => `${(s.start_time || '').trim()}–${(s.end_time || '').trim()}`.replace(/^–$/, '')).filter(Boolean).join(' + ');
          return (
            <div className="flex-1 overflow-auto mt-3" data-testid="schedule-output">
              <div className="mb-3 p-3 rounded-xl" style={{ backgroundColor: '#EAF2FF', border: '1px solid #C9D6E6', fontFamily: 'Manrope' }}>
                <p className="text-sm font-bold" style={{ color: accent }}>{schedule.headline_metric}</p>
                {schedule.summary && <p className="text-xs text-gray-700 mt-1">{schedule.summary}</p>}
              </div>
              {schedule.costing && (() => {
                const c = schedule.costing;
                const cur = c.currency || 'USD';
                const fmt = (n) => `${cur} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                const over = c.over_budget === true;
                const hasBudget = typeof c.weekly_budget === 'number';
                return (
                  <div
                    className="mb-3 p-3 rounded-xl"
                    data-testid="schedule-costing"
                    style={{ backgroundColor: over ? '#FEF2F2' : '#F0FDF4', border: `1px solid ${over ? '#FCA5A5' : '#BBF7D0'}`, fontFamily: 'Manrope' }}
                  >
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Total labor cost</p>
                        <p className="text-lg font-extrabold" style={{ color: over ? '#B91C1C' : '#15803D' }} data-testid="schedule-total-cost">{fmt(c.total_labor_cost)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Hours</p>
                        <p className="text-sm font-bold text-gray-800">{c.total_hours}h {c.overtime_hours > 0 ? <span className="text-amber-600">· {c.overtime_hours}h OT</span> : null}</p>
                      </div>
                      {hasBudget && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Budget</p>
                          <p className="text-sm font-bold text-gray-800">{fmt(c.weekly_budget)} · <span style={{ color: over ? '#B91C1C' : '#15803D' }}>{c.pct_of_budget}%</span></p>
                        </div>
                      )}
                      {hasBudget && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Variance</p>
                          <p className="text-sm font-extrabold" style={{ color: over ? '#B91C1C' : '#15803D' }}>{c.variance > 0 ? '+' : ''}{fmt(c.variance)} {over ? 'OVER' : 'under'}</p>
                        </div>
                      )}
                    </div>
                    {(c.unrated_staff || []).length > 0 && (
                      <p className="text-[11px] mt-2 text-amber-700" data-testid="schedule-unrated">
                        ⚠ No pay rate set for: {c.unrated_staff.join(', ')} — their cost is counted as 0. Add rates in the team roster for accurate totals.
                      </p>
                    )}
                  </div>
                );
              })()}
              {/* Matrix table — staff rows × day columns. Hours of work sit in
                  the middle cells; "OFF" placeholder for days a staff member
                  isn't scheduled. Mirrors what the exported PDF/Excel/Word
                  files render so admins can compare on-screen and exported. */}
              <div className="overflow-auto rounded-lg border" style={{ borderColor: '#E2DBC8' }} data-testid="schedule-matrix">
                <table className="w-full text-xs border-collapse" style={{ fontFamily: 'Manrope' }}>
                  <thead style={{ background: accent, color: 'white' }}>
                    <tr>
                      <th className="px-2 py-2 text-left font-bold sticky left-0 z-10" style={{ background: accent, minWidth: 140 }}>Staff</th>
                      <th className="px-2 py-2 text-left font-bold" style={{ minWidth: 110 }}>Position</th>
                      {dayHeaders.map((d) => (
                        <th key={d.label} className="px-2 py-2 text-center font-bold" style={{ minWidth: 92 }}>
                          {d.label}
                          <span className="block text-[8px] font-normal opacity-85">{d.date.slice(5)}</span>
                        </th>
                      ))}
                      <th className="px-2 py-2 text-center font-bold" style={{ minWidth: 64 }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrixRows.length === 0 && (
                      <tr><td colSpan={2 + dayHeaders.length + 1} className="px-3 py-4 text-center italic text-gray-500">No staff or shifts produced.</td></tr>
                    )}
                    {matrixRows.map((r, idx) => {
                      const alt = idx % 2 === 1;
                      return (
                        <tr key={r.name} data-testid={`schedule-matrix-row-${idx}`} style={{ background: alt ? '#F4F8FD' : 'white' }}>
                          <td className="px-2 py-1.5 font-bold sticky left-0" style={{ background: alt ? '#F4F8FD' : 'white', color: '#1A2E2E' }}>{r.name}</td>
                          <td className="px-2 py-1.5 italic" style={{ color: '#4A5D5D' }}>{r.position || ''}</td>
                          {dayHeaders.map((dh) => {
                            const txt = fmtCell(matrix[r.name][dh.label]);
                            return (
                              <td
                                key={dh.label}
                                className="px-2 py-1.5 text-center"
                                style={txt
                                  ? { color: '#1A2E2E' }
                                  : { color: '#B0B5BD', background: '#FAFAFA', fontStyle: 'italic' }}
                                data-testid={`schedule-cell-${idx}-${dh.label}`}
                              >
                                {txt || 'OFF'}
                              </td>
                            );
                          })}
                          <td className="px-2 py-1.5 text-center font-bold" style={{ color: accent }}>{totals[r.name] || 0} h</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {(schedule.coverage_notes || []).length > 0 && (
                <div className="mt-3">
                  <h3 className="text-[10px] uppercase tracking-wider font-bold text-blue-700 mb-1">Coverage notes</h3>
                  <ul className="text-xs space-y-1">{schedule.coverage_notes.map((n, i) => <li key={i} className="text-gray-700">• {n}</li>)}</ul>
                </div>
              )}
              {(schedule.recommendations || []).length > 0 && (
                <div className="mt-3">
                  <h3 className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-1">Recommendations</h3>
                  <ul className="text-xs space-y-1">{schedule.recommendations.map((n, i) => <li key={i} className="text-gray-700">• {n}</li>)}</ul>
                </div>
              )}
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? 'md:col-span-2' : ''}`}>
      <span className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>{label}</span>
      {children}
    </label>
  );
}

function ExportItem({ label, icon, onClick, testid, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-2 w-full px-3 py-2 text-xs font-bold text-left hover:bg-blue-50 transition-colors"
      style={{ color, fontFamily: 'Manrope' }}
    >
      {icon} {label}
    </button>
  );
}
